import * as R from 'ramda';
import React, {
  memo,
  useMemo,
  Fragment,
  useState,
  useEffect,
  useCallback,
} from 'react';
// components
import { TextComponent } from '../../../../components';
import FilePreview from '../../../../components/document-preview/file-preview';
import {
  AddDocument2,
  renderConfirmationModal,
} from '../../../../components/activities';
// helpers/constants
import * as G from '../../../../helpers';
// icons
import * as I from '../../../../svgs';
// ui
import { Box, Flex, scrollableContainerCss4px } from '../../../../ui';
// utilities
import endpointsMap from '../../../../utilities/endpoints';
// feature fleet
import GoToConfiguration from './go-to-configuration';
import { prepareAndSetMaintenanceDocs } from './ai-upload';
//////////////////////////////////////////////////

const darkGreyColor = G.getTheme('colors.#7D828C');
const lightGreyColor = G.getTheme('colors.lightGrey');
const greyMatterhornColor = G.getTheme('colors.greyMatterhorn');

const wrapperStyles = {
  mb: 10,
  p: '7px',
  borderRadius: '4px',
  border: '1px solid',
  color: greyMatterhornColor,
  borderColor: lightGreyColor,
};

const Document = memo((props) => {
  const {
    index,
    document,
    openModal,
    closeModal,
    handleRemoveDocument,
    setMaintenanceDocument,
  } = props;

  const { file } = document;

  const { name, lastModified } = file;

  const formattedCreatedDate = G.convertDateTimeToConfigFormat(lastModified);

  return (
    <Flex {...wrapperStyles} fontSize={12}>
      <Box width='100%'>
        <Flex justifyContent='space-between'>
          <TextComponent withEllipsis={true} title={name} maxWidth='calc(100% - 75px)'>
            {name}
          </TextComponent>
          <Flex>
            <Box mr={10} cursor='pointer' onClick={() => setMaintenanceDocument(document)}>
              {I.eye(darkGreyColor)}
            </Box>
            <Box
              cursor='pointer'
              onClick={() =>
                renderConfirmationModal({ openModal, closeModal, action: () => handleRemoveDocument(index) })
              }
            >
              {I.trash(darkGreyColor)}
            </Box>
          </Flex>
        </Flex>
        <Flex mt='5px'>
          <TextComponent
            withEllipsis={true}
            color={darkGreyColor}
            maxWidth='calc(40% - 45px)'
            title={formattedCreatedDate}
          >
            {formattedCreatedDate}
          </TextComponent>
        </Flex>
      </Box>
    </Flex>
  );
});

const DocumentPreview = (props) => {
  const {
    entityType,
    isEditMode,
    documentURL,
    setDocumentURL,
    updateDocument,
    maintenanceDocument,
    setMaintenanceDocument,
    handleDownloadOrPreviewDocument,
  } = props;

  const { guid, file, documentFilename } = maintenanceDocument;

  const handleClose = useCallback(() => {
    setDocumentURL(null);
    setMaintenanceDocument(null);
  }, []);

  const updateMaintenanceDocument = useCallback((file) => {
    updateDocument(file, maintenanceDocument);

    handleClose();
  }, [handleClose, updateDocument, maintenanceDocument]);

  useEffect(() => {
    if (G.isFunction(handleDownloadOrPreviewDocument)) {
      handleDownloadOrPreviewDocument({
        documentFilename,
        returnObjectURL: true,
        endpoint: endpointsMap.fleetEquipmentServiceEntityDocumentDownload(guid, entityType),
      });
    }
  }, [guid, entityType, handleDownloadOrPreviewDocument]);

  const type = R.prop('type', file) || G.getFileMimeType(documentFilename);

  if (G.isNilOrEmpty(documentURL)) return null;

  const doc = {
    file: { type },
    preview: documentURL,
  };

  return (
    <Box
      p={15}
      height='calc(100% - 70px)'
    >
      <FilePreview
        doc={doc}
        handleClose={handleClose}
        isEditEnabled={isEditMode}
        updateImage={updateMaintenanceDocument}
      />
    </Box>
  );
};

const Documents = (props) => {
  const {
    openModal,
    closeModal,
    entityType,
    updateDocument,
    maintenanceDocs,
    setMaintenanceDocs,
    documentsDefaultDocumentType,
  } = props;

  const [maintenanceDocument, setMaintenanceDocument] = useState();

  const maintenanceDocumentURL = useMemo(
    () => R.propOr(null, 'preview', maintenanceDocument),
    [maintenanceDocument],
  );

  const handleRemoveDocument = useCallback((index) => {
    const newFiles = maintenanceDocs.filter((_, i) => i !== index);

    setMaintenanceDocs(newFiles);
  }, [maintenanceDocs, setMaintenanceDocs]);

  if (G.isNotNilAndNotEmpty(maintenanceDocumentURL)) {
    return (
      <DocumentPreview
        isEditMode={false}
        entityType={entityType}
        setDocumentURL={() => {}}
        updateDocument={updateDocument}
        documentURL={maintenanceDocumentURL}
        maintenanceDocument={maintenanceDocument}
        setMaintenanceDocument={setMaintenanceDocument}
      />
    );
  }

  return (
    <Fragment>
      {
        G.isNilOrEmpty(documentsDefaultDocumentType) && <GoToConfiguration entityType={entityType} />
      }
      {
        G.isNotNilAndNotEmpty(maintenanceDocs) &&
        <Box
          mr={10}
          overflowY='auto'
          p='15px 5px 0 15px'
          height='calc(100% - 200px)'
          css={scrollableContainerCss4px}
        >
          {
            maintenanceDocs.map((document, index) => (
              <Document
                key={index}
                index={index}
                document={document}
                openModal={openModal}
                closeModal={closeModal}
                handleRemoveDocument={handleRemoveDocument}
                setMaintenanceDocument={setMaintenanceDocument}
              />
            ))
          }
        </Box>
      }
      {
        G.isNotNilAndNotEmpty(documentsDefaultDocumentType) &&
        <AddDocument2
          onDropHandler={(files) => {
            prepareAndSetMaintenanceDocs({
              files,
              maintenanceDocs,
              setMaintenanceDocs,
              defaultDocumentType: documentsDefaultDocumentType,
            });
          }}
        />
      }
    </Fragment>
  );
};

export default Documents;

export { DocumentPreview };
